/**
 * Wiki release history shown in the header version badge (newest first).
 * When shipping a release: prepend a new entry and keep prior versions below.
 */
export const WIKI_VERSION_HISTORY = [
  {
    version: "2.8",
    date: "2026-10-16",
    label: "October 16, 2026",
    changes: [
      "Document library: Imperva competitive category (vendor slug imperva) — upload PDFs in Manage, filter in Search, and Q&A routes Imperva product keywords to the competitive index.",
      "Imperva docs crawl (docs-cybersec.thalesgroup.com) into the competitive document library: Cloud Application Security, current on-prem WAF v16.0, AI Security Fabric, Account Takeover, API Security, and Client-Side Protection.",
      "Imperva Q&A aliases include imperva, incapsula, securesphere, secure sphere, threatradar, and ai security fabric.",
    ],
  },
  {
    version: "2.7",
    date: "2026-10-13",
    label: "October 13, 2026",
    changes: [
      "Document library: Akamai competitive category (vendor slug akamai) — upload PDFs in Manage, filter in Search, and Q&A routes Akamai product keywords to the competitive index.",
      "Akamai TechDocs crawl (techdocs.akamai.com) into the competitive document library, covering security, CDN, DNS, and AI security docs.",
      "Akamai Q&A aliases include akamai, prolexic, kona, kona site defender, app & api protector, guardicore, edge dns, and firewall for ai.",
    ],
  },
  {
    version: "2.6",
    date: "2026-08-24",
    label: "August 24, 2026",
    changes: [
      "Q&A recognizes cloudWAF / cloud-waf as Cloud WAF instead of falling back to General Topics.",
      "Q&A no longer drops long answer paragraphs, which made some replies look empty.",
    ],
  },
  {
    version: "2.5",
    date: "2026-08-24",
    label: "August 24, 2026",
    changes: [
      "Log out button in the header clears the session immediately instead of waiting for the token to expire.",
    ],
  },
  {
    version: "2.4",
    date: "2026-08-24",
    label: "August 24, 2026",
    changes: [
      "Access page for granting and revoking wiki login emails without restarting the backend.",
      "The Access page is limited to designated admin accounts and only when the wiki is opened at a 172.30.4.x management address.",
    ],
  },
  {
    version: "2.3",
    date: "2026-08-19",
    label: "August 19, 2026",
    changes: [
      "RFP Feeder: Check compliance sends reviewed Technical/Sizing requirements to the App 3 portal and shows Compliant / Partial / Non-compliant chips on the Review grid.",
      "Click a chip for the English explanation and citations; re-check one row or the whole RFP. Results stay on the job and do not overwrite SE Comment.",
    ],
  },
  {
    version: "2.2",
    date: "2026-08-12",
    label: "August 12, 2026",
    changes: [
      "RFP Feeder: export an Excel workbook with product and class dropdowns for offline review; import the same file to update, add, or delete requirements.",
      "Excel import matches rows by hidden id, reports invalid product/class values, and marks analysis stale when requirements change.",
      "Excel import errors stay on the RFP page (row, field, reason) with a Download error report button.",
    ],
  },
  {
    version: "2.1",
    date: "2026-08-07",
    label: "August 7, 2026",
    changes: [
      "RFP Feeder upload: mandatory Owner SE (fixed list), Customer (CRM name), and Country (CALA country list); optional notification email, page ranges, and RFP version (v1–v5).",
      "RFP Feeder: upload blocked until required fields are complete; empty email skips completion notification; existing library jobs without country show as CALA.",
      "Document library: Fortinet competitive category (vendor slug fortinet) — upload PDFs in Manage, filter in Search, and Q&A routes Fortinet product keywords to the competitive index.",
      "Fortinet Q&A aliases include fortinet, forti, fortiweb, fortiwaf, fortiddos, fortiadc, fortimanager, fortianalyzer, fortiappsec, fortiguard, and fortiai.",
    ],
  },
  {
    version: "2.0",
    date: "2026-06-17",
    label: "June 17, 2026",
    changes: [
      "RFP Feeder Process: replaced n8n workflow with a local Python worker (extract → structural preprocess → LLM classify → CSV); PDFs stored per job for reprocess; upload requires customer name and owner SE.",
      "RFP Feeder Process: full review UI — job library, workflow stepper, requirement grid with product/class filters, PDF panel, sticky save, row reorder, and SE comments.",
      "RFP Feeder Process: Radware product taxonomy (Cloud WAF, DefensePro, Cyber Controller, etc.) and nine requirement classes with LLM-only classification (gpt-5.4-mini) plus sanitizer post-processing.",
      "RFP Feeder Process: Spanish implicit requirement support — capability fragments and bot-management bullets classified as Technical; section headings kept Non-relevant.",
      "RFP Feeder Analysis: post-review analysis pipeline — prepare input snapshot, hybrid retrieval (FAISS + keywords), evidence bundles, and tabbed LLM generation with executive summary.",
      "RFP Feeder Analysis: technology-boundary validation, management-scope coverage, volume-metric normalization, and analysis status tracking per job.",
    ],
  },
  {
    version: "1.3",
    date: "2026-06-17",
    label: "June 17, 2026",
    changes: [
      "Q&A source selection simplified: Wiki and Cloudflare checkboxes removed; wiki/manual + document RAG always runs on every question.",
      "Cloudflare n8n RAG auto-triggers when the question mentions cloudflare, API Shield, or Magic Transit — returns both wiki and Cloudflare answer cards.",
      "Updated Q&A helper text: competitors must be named explicitly in the question to search the competitive database.",
    ],
  },
  {
    version: "1.2",
    date: "2026-06-15",
    label: "June 15, 2026",
    changes: [
      "F5 Distributed Cloud web docs: local crawl-and-ingest pipeline (docs.cloud.f5.com) into the competitive document library — ~2,500 URLs crawled, chunked, and embedded for Q&A.",
      "Q&A source links fixed: crawled F5 pages open in the browser (https://), manual wiki sections open inline, PDFs download; Sources list shows cited sources only (max 5) via server-built display_sources.",
      "Automated ingest script (f5_web_ingest_auto.py) with resume, deferred failure handling, and verbose embed progress logging (f5_web_embed.py).",
    ],
  },
  {
    version: "1.1",
    date: "2026-06-12",
    label: "June 12, 2026",
    changes: [
      "Expanded F5 competitive document routing: BIG-IP, rSeries, iSeries, LTM, GTM, ASM, AFM, nginx, Distributed Cloud, Volterra, Silverline, Shape Security, and related terms now select F5 vendor documents in Q&A.",
      "Version badge added to the wiki header — hover or click to view release notes on Home, Q&A, Editor, RFP, and Activity.",
    ],
  },
  {
    version: "1.0",
    date: "2026-05-23",
    label: "May 23, 2026",
    changes: [
      "Initial Radware Wiki release: Home, Q&A, Editor, RFP, and Activity pages with shared navigation.",
      "Q&A over internal wiki content plus Radware and competitive document libraries (F5, Arbor) with automatic product-family routing.",
      "Document library for upload, metadata, chunking, and embedding of Radware and competitor PDFs.",
      "Wiki Editor for article maintenance and RFP Feeder for structured response workflows.",
    ],
  },
];

/** Current (latest) release — first entry in history. */
export const WIKI_VERSION = WIKI_VERSION_HISTORY[0];

/** Prior releases for the history section. */
export const WIKI_VERSION_PRIOR = WIKI_VERSION_HISTORY.slice(1);
